import React, { lazy } from "react";
import { Navigate } from "react-router-dom";
import { FeatureFlagRouteGuard } from "../components/shared/route-guards/FeatureFlagRouteGuard";

function withFeatureFlag(flag, Component) {
  const Wrapped = () => (
    <FeatureFlagRouteGuard flag={flag}>
      <Component />
    </FeatureFlagRouteGuard>
  );
  return Wrapped;
}

/* Heavy pages — code-split for faster initial load */
const StripeOnboard = lazy(() => import("../components/pages/admin/users/project-payments/onboard-freelancer/StripeOnboard"));
const ProjectProposals = lazy(() => import("../components/pages/browse-projects/gig/client/ProjectProposals").then((m) => ({ default: m.ProjectProposals })));
const ReviewPage = lazy(() => import("../components/pages/browse-projects/gig/client/ReviewPage").then((m) => ({ default: m.ReviewPage })));
const ProjectGig = lazy(() => import("../components/pages/browse-projects/gig/freelancer/ProjectGig").then((m) => ({ default: m.ProjectGig })));
const Dashboard = lazy(() => import("../components/pages/Dashboard/Dashboard").then((m) => ({ default: m.Dashboard })));
const MyJobApplications = lazy(() => import("../components/pages/Dashboard/job-applications/MyJobApplications").then((m) => ({ default: m.MyJobApplications })));
const JobsList = lazy(() => import("../components/pages/Dashboard/job-listing-management").then((m) => ({ default: m.JobsList })));
const CreateJobListing = lazy(() => import("../components/pages/Dashboard/job-listing-management/CreateJobListing").then((m) => ({ default: m.CreateJobListing })));
const ViewJobList = lazy(() => import("../components/pages/Dashboard/job-listing-management/ViewJobList").then((m) => ({ default: m.ViewJobList })));
const Profile = lazy(() => import("../components/pages/Dashboard/Profile").then((m) => ({ default: m.Profile })));
const AddCourse = lazy(() => import("../components/pages/freelancer-courses/AddCourse").then((m) => ({ default: m.AddCourse })));
const FreelancerCourseList = lazy(() => import("../components/pages/freelancer-courses/FreelancerCourseList").then((m) => ({ default: m.FreelancerCourseList })));
const ViewCourse = lazy(() => import("../components/pages/freelancer-courses/ViewCourse").then((m) => ({ default: m.ViewCourse })));
const VettingLandingPage = lazy(() => import("../components/pages/vetting/VettingLandingPage"));
const VettingApplicationPage = lazy(() => import("../components/pages/vetting/VettingApplicationPage"));
const ApplicationPage = lazy(() => import("../components/pages/job-listing/ApplicationPage").then((m) => ({ default: m.ApplicationPage })));
const Messages = lazy(() => import("../components/pages/messages/Messages").then((m) => ({ default: m.Messages })));
const BusinessPaymentsList = lazy(() => import("../components/pages/payments/business/BusinessPaymentsList").then((m) => ({ default: m.BusinessPaymentsList })));
const Payments = lazy(() => import("../components/pages/payments/Payments").then((m) => ({ default: m.Payments })));
const AddProject = lazy(() => import("../components/pages/projects/AddProject").then((m) => ({ default: m.AddProject })));
const Projects = lazy(() => import("../components/pages/projects/Projects").then((m) => ({ default: m.Projects })));
const ViewProject = lazy(() => import("../components/pages/projects/ViewProject").then((m) => ({ default: m.ViewProject })));
const CheckoutForm = lazy(() => import("../components/pages/subscription/CheckoutForm").then((m) => ({ default: m.CheckoutForm })));
const ChoosePlan = lazy(() => import("../components/pages/subscription/ChoosePlan").then((m) => ({ default: m.ChoosePlan })));
const ChangePlanPage = lazy(() => import("../components/pages/subscription/ChangePlanPage").then((m) => ({ default: m.ChangePlanPage })));
const SubscriptionPage = lazy(() => import("../components/pages/subscription/SubscriptionPage"));
const SubscriptionSettings = lazy(() => import("../components/pages/subscription/SubscriptionSettings"));
const RecurringCheckoutPage = lazy(() => import("../components/pages/subscription/RecurringCheckoutPage"));
const UserList = lazy(() => import("../components/pages/user-management").then((m) => ({ default: m.UserList })));
const ServicesManager = lazy(() => import("../components/pages/Dashboard/freelancer-services/ServicesManager").then((m) => ({ default: m.ServicesManager })));
const FreelancerMyProjects = lazy(() => import("../components/pages/my-projects/FreelancerMyProjects"));
const ProposalWorkspaceLegacyRedirect = lazy(() => import("../components/shared/routing/ProposalWorkspaceLegacyRedirect"));
const NotificationsPage = lazy(() => import("../components/pages/notifications/NotificationsPage"));
const FreelancerProfilePage = lazy(() => import("../components/pages/browse-freelancers/FreelancerProfilePage").then((m) => ({ default: m.FreelancerProfilePage })));
const SavedFreelancersPage = lazy(() => import("../components/pages/saved/SavedFreelancersPage").then((m) => ({ default: m.SavedFreelancersPage })));
const SavedProjectsPage = lazy(() => import("../components/pages/saved/SavedProjectsPage").then((m) => ({ default: m.SavedProjectsPage })));

export const PRIVATE_ROUTES = [
  {
    path: "/users",
    element: UserList,
  },
  {
    path: "/dashboard",
    element: Dashboard,
  },
  {
    path: "/notifications",
    element: NotificationsPage,
  },
  {
    path: "/job-application",
    element: ApplicationPage,
  },
  {
    path: "/my-projects/:proposalId",
    element: FreelancerMyProjects,
  },
  {
    path: "/my-projects",
    element: FreelancerMyProjects,
  },
  {
    path: "/projects",
    element: Projects,
  },
  {
    path: "/add-project",
    element: AddProject,
  },
  {
    path: "/update-project",
    element: AddProject,
  },
  {
    path: "/view-project",
    element: ViewProject,
  },
  {
    path: "/profile",
    element: Profile,
  },
  {
    path: "/jobs",
    element: JobsList,
  },
  {
    path: "/create-job",
    element: CreateJobListing,
  },
  {
    path: "/update-job",
    element: CreateJobListing,
  },
  {
    path: "/payments",
    element: Payments,
  },
  {
    path: "/view-applicants",
    element: ViewJobList,
  },
  {
    path: "/job-applications",
    element: MyJobApplications,
  },
  {
    path: "/track-payments",
    element: BusinessPaymentsList,
  },
  {
    path: "/messages",
    element: withFeatureFlag("messaging", Messages),
  },
  {
    path: "/my-services",
    element: ServicesManager,
  },
  {
    path: "/freelancer-courses",
    element: withFeatureFlag("courses", FreelancerCourseList),
  },
  {
    path: "/publish-requests",
    element: withFeatureFlag("courses", FreelancerCourseList),
  },
  {
    path: "/create-course",
    element: withFeatureFlag("courses", AddCourse),
  },
  {
    path: "/update-course",
    element: withFeatureFlag("courses", AddCourse),
  },

  {
    path: "/view-course",
    element: withFeatureFlag("courses", ViewCourse),
  },
  {
    path: "/vetting",
    element: VettingLandingPage,
  },
  {
    path: "/vetting/apply",
    element: VettingApplicationPage,
  },
  {
    path: "/subscribe",
    element: ChoosePlan,
  },
  {
    path: "/subscription",
    element: ChoosePlan,
  },
  {
    path: "/subscription-settings",
    element: SubscriptionSettings,
  },
  {
    path: "/subscription/change-plan",
    element: ChangePlanPage,
  },
  {
    path: "/register/freelancer/profile",
    element: Profile,
  },
  {
    path: "/checkout",
    element: CheckoutForm,
  },
  {
    path: "/checkout-form",
    element: SubscriptionPage,
  },
  {
    path: "/subscribe-checkout",
    element: RecurringCheckoutPage,
  },
  {
    path: "/subscribers",
    element: () => <Navigate to="/admin/subscribers" replace />,
  },
  {
    path: "/gigs",
    element: ProjectGig,
  },
  {
    path: "/gig-proposals",
    element: ProposalWorkspaceLegacyRedirect,
  },
  {
    path: "/gig-proposals/:proposalId",
    element: ProposalWorkspaceLegacyRedirect,
  },
  {
    path: "/project-proposals/:proposalId",
    element: FreelancerMyProjects,
  },
  {
    path: "/project-proposals",
    element: ProjectProposals,
  },

  {
    path: "/review",
    element: ReviewPage,
  },
  {
    path: "/gig-payments",
    element: () => <Navigate to="/admin/finance" replace />,
  },
  { path: "/stripe-onboard", element: StripeOnboard },
  { path: "/skills-management", element: () => <Navigate to="/admin/skills" replace /> },
  { path: "/freelancer-profile/:id", element: FreelancerProfilePage },
  { path: "/saved-freelancers", element: SavedFreelancersPage },
  { path: "/saved-projects", element: SavedProjectsPage },
];
